import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface RotatingTextAnimationProps {
  /** Array of text phrases to rotate through */
  phrases: string[];
  /** Time in milliseconds between phrase transitions */
  interval?: number;
  /** Additional CSS classes to apply to the animated text */
  className?: string;
  /** Whether the animation should automatically start */
  autoPlay?: boolean;
  /** Whether to disable blur effect during transitions */
  disableBlur?: boolean;
  /** Callback fired when phrase changes */
  onPhraseChange?: (index: number, phrase: string) => void;
  /** Whether to shuffle the phrases order */
  shuffle?: boolean;
}

/**
 * RotatingTextAnimation - A smooth text rotation component with fade and slide transitions
 *
 * @example
 * <RotatingTextAnimation
 *   phrases={["Hello", "World", "React"]}
 *   interval={2000}
 *   className="text-lg font-bold"
 * />
 */
const RotatingTextAnimation: React.FC<RotatingTextAnimationProps> = ({
  phrases,
  interval = 3000,
  className = "",
  autoPlay = true,
  disableBlur = false,
  onPhraseChange,
  shuffle = false,
}) => {
  const shuffledPhrases = useMemo(() => {
    if (!shuffle) {
      return phrases;
    }
    const shuffled = [...phrases];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, [phrases, shuffle]);

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!autoPlay || shuffledPhrases.length <= 1) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentPhraseIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % shuffledPhrases.length;
        onPhraseChange?.(nextIndex, shuffledPhrases[nextIndex]);
        return nextIndex;
      });
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [shuffledPhrases, interval, autoPlay, onPhraseChange]);

  if (!phrases?.length) {
    return null;
  }

  const animationVariants = {
    initial: {
      y: 20,
      opacity: 0,
      filter: disableBlur ? undefined : "blur(10px)",
    },
    animate: {
      y: 0,
      opacity: 1,
      filter: disableBlur ? undefined : "blur(0px)",
    },
    exit: {
      y: -20,
      opacity: 0,
      filter: disableBlur ? undefined : "blur(10px)",
    },
  };

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={`phrase-${currentPhraseIndex}`}
        variants={animationVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{
          duration: 0.3,
          ease: "easeInOut",
          opacity: { duration: 0.25 },
        }}
        className={`block ${className}`}
        aria-live="polite"
        aria-atomic="true"
      >
        {shuffledPhrases[currentPhraseIndex]}
      </motion.span>
    </AnimatePresence>
  );
};

export default RotatingTextAnimation;
